import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { RemoteSelect } from '@/components/ui/remote-select';
import { assetProcurementApi } from '@/lib/assetProcurementApi';
import { useT } from '@/i18n/LanguageContext';

/**
 * <SupplierPicker> — 仕入先（注文先）（仕様 2.1 / 2.6 #12）。
 *
 * 既に共有されている仕入先マスター（`mfg_suppliers`）が正本。未登録の店は
 * 自由入力で確定でき、権限があればその場でマスターに追加できる。
 * （`components/ProcurementFields` の仕入先ブロックと同じ約束・同じ口。）
 */
export const SupplierPicker = ({ value, onChange, testId = 'supplier-picker' }) => {
  const { t } = useT();
  const [nonce, setNonce] = useState(0);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [open, setOpen] = useState(false);
  const fetchSuppliers = useCallback(() => assetProcurementApi.suppliers(), [nonce]); // eslint-disable-line react-hooks/exhaustive-deps
  const patch = (p) => onChange?.({ ...value, ...p });

  const add = async () => {
    const name = newName.trim();
    if (!name) { toast.error('仕入先名を入力してください'); return; }
    setAdding(true);
    try {
      const s = await assetProcurementApi.createSupplier({ name });
      setNonce((n) => n + 1);
      patch({ supplier_id: s.id, supplier_name: s.name });
      setNewName(''); setOpen(false);
      toast.success(`仕入先「${s.name}」を追加しました`);
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || '仕入先の追加に失敗しました（製造マスタの編集権限が必要です）');
    } finally { setAdding(false); }
  };

  return (
    <div data-testid={testId}>
      <RemoteSelect fetch={fetchSuppliers} value={value?.supplier_id || null}
        onChange={(o) => patch({ supplier_id: o?.id || '', supplier_name: o?.name || '' })}
        placeholder={t('reg.supplier.ph')} allowClear testId={`${testId}-select`}
        emptyHint="仕入先マスタから追加してください" />
      <Input value={value?.supplier_id ? '' : (value?.supplier_name || '')}
        onChange={(e) => patch({ supplier_name: e.target.value })}
        disabled={!!value?.supplier_id}
        placeholder={t('reg.supplier.free')}
        className="h-9 mt-1 text-xs" data-testid={`${testId}-free`} />
      {!value?.supplier_id && (value?.supplier_name || '').trim() && (
        <p className="mt-1 text-[11px] text-amber-700" data-testid={`${testId}-unregistered`}>
          {t('reg.supplier.unregistered', { name: value.supplier_name })}
        </p>
      )}
      {open ? (
        <div className="mt-1.5 flex gap-1.5" data-testid={`${testId}-new-row`}>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder={t('reg.supplier.newPh')} className="h-9 text-xs" data-testid={`${testId}-new-input`} />
          <button type="button" onClick={add} disabled={adding}
            className="h-9 shrink-0 rounded-md bg-teal-700 px-3 text-xs font-semibold text-white hover:bg-teal-800"
            data-testid={`${testId}-new-save`}>{adding ? t('reg.adding') : t('reg.add')}</button>
          <button type="button" onClick={() => setOpen(false)}
            className="h-9 shrink-0 rounded-md border border-zinc-200 px-3 text-xs text-zinc-600"
            data-testid={`${testId}-new-cancel`}>{t('reg.stop')}</button>
        </div>
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          className="mt-1 text-[11px] font-semibold text-teal-700 hover:underline"
          data-testid={`${testId}-new-open`}>{t('reg.supplier.add')}</button>
      )}
    </div>
  );
};

export default SupplierPicker;
